import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { removeSampleTestArray } from "../../actions/cleanup";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailTaxesTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import { PercentageorFlatAmount } from "../../../constants/GuaranteeField";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";

const RetailTaxes = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [outlet, setOutlet] = useState<string | null>(null);
  const [taxName, setTaxName] = useState<string | null>(null);
  const [allowTaxOnServiceCharge, setAllowTaxOnServiceCharge] =
    useState<boolean>(false);
  const [allowTaxOnGratuity, setAllowTaxOnGratuity] = useState<boolean>(false);
  const [percentageOrFlatAmount, setPercentageOrFlatAmount] = useState<
    string | null
  >(null);
  const [taxValue, setTaxValue] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(() => dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(() => dayjs());
  const [defaultToAllRetailItems, setDefaultToAllRetailItems] =
    useState<boolean>(false);
  const [outletErr, setOutletErr] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [percentageErr, setPercentageErr] = useState<boolean>(false);
  const [valueErr, setValueErr] = useState<boolean>(false);
  const [startDateErr, setStartDateErr] = useState<boolean>(false);
  const [endDateErr, setEndDateErr] = useState<boolean>(false);
  const [retailTaxes, setRetailTaxes] = useState<any>([]);
  const [outletList, setOutletList] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const payload = {
    Outlet: outlet,
    "Tax Name": taxName,
    "Allow Tax on Service Charge": allowTaxOnServiceCharge ? "Yes" : "No",
    "Allow Tax on Gratuity": allowTaxOnGratuity ? "Yes" : "No",
    "Percentage or Flat Amount": percentageOrFlatAmount,
    Value: taxValue,
    "Start Date": startDate ? startDate.toISOString() : "",
    "End Date": endDate ? endDate.toISOString() : "",
    "Default to all Retail Items": defaultToAllRetailItems ? "Yes" : "No",
  };

  const validateInput = () => {
    setOutletErr(false);
    setNameError(false);
    setPercentageErr(false);
    setValueErr(false);
    setStartDateErr(false);
    setEndDateErr(false);
    let ok = true;
    if (!outlet || String(outlet).trim() === "") {
      setOutletErr(true);
      ok = false;
    } else if (
      !validateString(
        String(outlet).trim(),
        validationLength.outletName,
        validationType.SPACE,
      )
    ) {
      setOutletErr(true);
      ok = false;
    }
    if (!taxName || String(taxName).trim() === "") {
      setNameError(true);
      ok = false;
    } else if (
      !validateString(taxName, validationLength.taxName, validationType.SPACE)
    ) {
      setNameError(true);
      ok = false;
    }
    if (!percentageOrFlatAmount || percentageOrFlatAmount === "") {
      setPercentageErr(true);
      ok = false;
    }
    if (!taxValue || taxValue === "") {
      setValueErr(true);
      ok = false;
    } else if (!/^\d+(\.\d+)?$/.test(taxValue)) {
      setValueErr(true);
      ok = false;
    }
    if (!startDate) {
      setStartDateErr(true);
      ok = false;
    }
    if (!endDate) {
      setEndDateErr(true);
      ok = false;
    } else if (startDate && endDate.isBefore(startDate, "day")) {
      setEndDateErr(true);
      ok = false;
    }
    return ok;
  };

  const editRetailTaxes = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setOutlet(null);
      setTaxName(null);
      setAllowTaxOnServiceCharge(false);
      setAllowTaxOnGratuity(false);
      setPercentageOrFlatAmount(null);
      setTaxValue(null);
      setStartDate(dayjs());
      setEndDate(dayjs());
      setDefaultToAllRetailItems(false);
      setOutletErr(false);
      setNameError(false);
      setPercentageErr(false);
      setValueErr(false);
      setStartDateErr(false);
      setEndDateErr(false);
      return;
    }
    if (list !== null) {
      setEditClicked(true);
      if (list.id != null) {
        setSelectedId(list.id);
      } else {
        setSelectedId(null);
      }
    }
    setOutlet(
      list?.Outlet != null && list.Outlet !== "" ? String(list.Outlet) : null,
    );
    setTaxName(list?.["Tax Name"] ?? null);
    setAllowTaxOnServiceCharge(
      list?.["Allow Tax on Service Charge"] === "Yes" ||
        list?.["Allow Tax on Service Charge"] === true,
    );
    setAllowTaxOnGratuity(
      list?.["Allow Tax on Gratuity"] === "Yes" ||
        list?.["Allow Tax on Gratuity"] === true,
    );
    const p = list?.["Percentage or Flat Amount"];
    if (p === "%" || p === "Percentage") {
      setPercentageOrFlatAmount("Percentage");
    } else if (p === "$" || p === "Flat Amount") {
      setPercentageOrFlatAmount("Flat Amount");
    } else if (typeof p === "string" && PercentageorFlatAmount.includes(p)) {
      setPercentageOrFlatAmount(p);
    } else {
      setPercentageOrFlatAmount(null);
    }
    setTaxValue(
      list?.Value != null && list.Value !== "" ? String(list.Value) : null,
    );
    const loadedStart = list?.["Start Date"]
      ? dayjs(list["Start Date"])
      : dayjs();
    setStartDate(loadedStart);
    const loadedEndRaw = list?.["End Date"]
      ? dayjs(list["End Date"])
      : loadedStart;
    setEndDate(
      loadedEndRaw.isBefore(loadedStart, "day") ? loadedStart : loadedEndRaw,
    );
    setDefaultToAllRetailItems(
      list?.["Default to all Retail Items"] === "Yes" ||
        list?.["Default to all Retail Items"] === true,
    );
    setOutletErr(false);
    setNameError(false);
    setPercentageErr(false);
    setValueErr(false);
    setStartDateErr(false);
    setEndDateErr(false);
  };

  const updateRetailTaxes = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailTaxes.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.TAXNAME2,
        taxName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailTaxes((prev: any) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailTaxes(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RetailPropertyEnum.taxes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailTaxes = () => {
    if (!validateInput()) return;
    if (checkDuplicate(retailTaxes, DNDFieldsEnum.TAXNAME2, taxName)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailTaxes((prev: any) =>
      updateData(
        prev !== null && prev?.length
          ? [...prev, { id: retailTaxes.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailTaxes(null);
    setEditClicked(false);
  };

  const deleteRetailTaxes = (id: number) => {
    setRetailTaxes(deleteItem(retailTaxes, id));
  };

  useEffect(() => {
    if (fieldValues) {
      setRetailTaxes(fieldValues[RetailPropertyEnum.taxes]);
      setOutletList(
        fillAutocompleteValues(
          fieldValues[RetailPropertyEnum.outletSetup],
          DNDFieldsEnum.OUTLETNAME,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(retailTaxes);
    editClicked &&
      setSelectedId(checkData(retailTaxes, selectedId) ? selectedId : null);
  }, [retailTaxes]);

  const taxNameHelperText = nameError
    ? !taxName || String(taxName).trim() === ""
      ? validationMessages.fieldRequired
      : validationMessages.taxName
    : fieldValidationHelperText.taxName;

  const outletHelperText = outletErr
    ? !outlet || String(outlet).trim() === ""
      ? validationMessages.fieldRequired
      : validationMessages.outletName
    : "";

  const valueHelperText = valueErr
    ? !taxValue || taxValue === ""
      ? validationMessages.fieldRequired
      : validationMessages.retailTaxValueInvalid
    : "";

  const endDateHelperText = endDateErr
    ? !endDate
      ? validationMessages.fieldRequired
      : validationMessages.endDateBeforeStart
    : "";

  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Taxes
              <Tooltip title={tooltipComponent(retailTaxesTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailTaxes(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailTaxes(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Outlet
              <Tooltip title={tooltipComponent(retailTaxesTooltip.outlet)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Stack direction="row" width="100%" gap="5px">
              <Autocomplete
                id="retailTaxOutletList"
                disablePortal
                options={removeSampleTestArray(outletList)}
                sx={{ width: "100%" }}
                value={outlet}
                disabled={!outletList}
                onChange={(_e, newValue) => {
                  setOutlet(newValue ?? null);
                  setOutletErr(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Outlet"
                    variant="outlined"
                    placeholder="Outlet"
                    error={outletErr}
                    helperText={outletHelperText}
                  />
                )}
              />
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Tax Name
              <Tooltip title={tooltipComponent(retailTaxesTooltip.taxName)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailTaxName"
              variant="outlined"
              placeholder="Tax Name"
              value={taxName ?? ""}
              helperText={taxNameHelperText}
              error={nameError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTaxName(e.target.value || null);
                setNameError(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Allow Tax on Service Charge?
              <Tooltip
                title={tooltipComponent(
                  retailTaxesTooltip.allowTaxOnServiceCharge,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={allowTaxOnServiceCharge}
                  onChange={() =>
                    setAllowTaxOnServiceCharge(!allowTaxOnServiceCharge)
                  }
                />
              }
              label=""
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Allow Tax on Gratuity?
              <Tooltip
                title={tooltipComponent(retailTaxesTooltip.allowTaxOnGratuity)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={allowTaxOnGratuity}
                  onChange={() => setAllowTaxOnGratuity(!allowTaxOnGratuity)}
                />
              }
              label=""
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Percentage or Flat Amount
              <Tooltip
                title={tooltipComponent(
                  retailTaxesTooltip.percentageOrFlatAmount,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControl fullWidth error={percentageErr}>
              <Select
                id="retailTaxPercentageOrFlat"
                displayEmpty
                value={percentageOrFlatAmount ?? ""}
                fullWidth
                onChange={(e) => {
                  setPercentageOrFlatAmount(
                    e.target.value === "" ? null : String(e.target.value),
                  );
                  setPercentageErr(false);
                }}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {PercentageorFlatAmount.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {percentageErr ? (
                <FormHelperText>
                  {validationMessages.fieldRequired}
                </FormHelperText>
              ) : null}
            </FormControl>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Value
              <Tooltip title={tooltipComponent(retailTaxesTooltip.value)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailTaxValue"
              variant="outlined"
              placeholder="Value"
              value={taxValue ?? ""}
              helperText={valueHelperText}
              error={valueErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const raw = e.target.value;
                if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
                  setTaxValue(raw || null);
                  setValueErr(false);
                }
              }}
            />
          </Stack>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack>
              <FormLabel component="legend">
                Start Date
                <Tooltip title={tooltipComponent(retailTaxesTooltip.startDate)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <DatePicker
                value={startDate}
                onChange={(v) => {
                  setStartDate(v);
                  setStartDateErr(false);
                  if (v && endDate && endDate.isBefore(v, "day")) {
                    setEndDate(v);
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "Start Date",
                    error: startDateErr,
                    helperText: startDateErr
                      ? validationMessages.fieldRequired
                      : "",
                  },
                }}
              />
            </Stack>
            <Stack>
              <FormLabel component="legend">
                End Date
                <Tooltip title={tooltipComponent(retailTaxesTooltip.endDate)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <DatePicker
                value={endDate}
                minDate={startDate ?? undefined}
                onChange={(v) => {
                  setEndDateErr(false);
                  if (!v) {
                    setEndDate(null);
                    return;
                  }
                  if (startDate && v.isBefore(startDate, "day")) {
                    setAlertMessage(validationMessages.endDateBeforeStart);
                    setAlertType("error");
                    setOpenAlert(true);
                    setEndDate(startDate);
                    return;
                  }
                  setEndDate(v);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "End Date",
                    error: endDateErr,
                    helperText: endDateHelperText,
                  },
                }}
              />
            </Stack>
          </LocalizationProvider>

          <Stack>
            <FormLabel component="legend">
              Default to All Retail Items?
              <Tooltip
                title={tooltipComponent(
                  retailTaxesTooltip.defaultToAllRetailItems,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={defaultToAllRetailItems}
                  onChange={() =>
                    setDefaultToAllRetailItems(!defaultToAllRetailItems)
                  }
                />
              }
              label=""
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateRetailTaxes : addRetailTaxes}
              id="addRetailTax"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Tax
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Taxes Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailTaxes?.length ? (
                <DraggableItemList
                  itemData={retailTaxes}
                  onOrderUpdate={(order) => setRetailTaxes(order)}
                  onEdit={(info) => editRetailTaxes(info)}
                  onDelete={(id) => deleteRetailTaxes(id)}
                  code={DNDFieldsEnum.OUTLET}
                  name={DNDFieldsEnum.TAXNAME2}
                />
              ) : (
                <Typography variant="body2">
                  No Taxes Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailTaxes;
